import type { AppType } from "@archinventory/api";
import { type ClientResponse, hc, type InferResponseType } from "hono/client";

export type ApiClient = ReturnType<typeof hc<AppType>>;

/**
 * Typed Hono RPC client, shared by web (same origin, cookies) and mobile (absolute URL,
 * session cookie from @better-auth/expo passed via `headers`).
 */
export function createApi(opts: {
  baseUrl: string;
  fetch?: typeof fetch;
  headers?: () => Record<string, string> | Promise<Record<string, string>>;
}): ApiClient {
  return hc<AppType>(`${opts.baseUrl.replace(/\/$/, "")}/api`, {
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
    ...(opts.headers ? { headers: opts.headers } : {}),
    init: { credentials: "include" },
  });
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public issues?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Await an RPC call, throw ApiError on non-2xx, return the typed JSON body. */
export async function unwrap<T extends ClientResponse<unknown, number, "json">>(
  p: Promise<T>,
): Promise<InferResponseType<() => Promise<T>, 200 | 201>> {
  const res = await p;
  if (!res.ok) {
    let body: { error?: string; issues?: { path: string; message: string }[] } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {}
    throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`, body.issues);
  }
  return (await res.json()) as InferResponseType<() => Promise<T>, 200 | 201>;
}
